import type { ComponentProps, ElementType } from 'react'
import { classNames } from '~/utils/client/helpers'
import { SpinnerIcon } from '../Icons/Spinner'
import { Detail } from '../text/Text'

type PolymorphicAsProp<E extends ElementType> = {
  as?: E
}

export type ButtonProps<E extends ElementType> = React.ComponentProps<'button'> & {
  label: string
  variant?: 'primary' | 'secondary' | 'transparent'
  block?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'center' | 'right'
  loading?: boolean
  labelClass?: string
  size?: 'regular' | 'small' | 'large' | 'thin'
  align?: 'left' | 'right' | 'none'
  as?: E
} & Omit<ComponentProps<E>, 'as'> &
  PolymorphicAsProp<E>

export const Button = <As extends ElementType = 'button'>({
  label,
  variant = 'primary',
  size = 'regular',
  className = '',
  icon,
  iconPosition = 'left',
  loading,
  block,
  labelClass,
  align = 'none',
  as,
  disabled,
  ...buttonProps
}: ButtonProps<As>) => {
  const Element = as || 'button'
  if (loading && icon == null) {
    iconPosition = 'center'
  }

  if (loading) {
    icon = <SpinnerIcon />
  }

  const isDisabled = disabled || loading

  return (
    <Element
      {...buttonProps}
      disabled={isDisabled}
      className={classNames(
        `group relative flex justify-center border border-transparent text-sm font-medium focus-ring-brand items-center`,
        variant === 'primary'
          ? 'bg-brand text-white'
          : variant === 'secondary'
          ? 'bg-accent-muted text-body hover:bg-surface-inset'
          : 'bg-white text-secondary hover:bg-surface-inset',
        !loading && (variant === 'primary' ? 'hover:bg-brand-hover' : ''),
        loading && 'opacity-75 cursor-wait',
        isDisabled && 'cursor-not-allowed opacity-75',
        isDisabled && variant === 'primary' && 'hover:bg-brand',
        isDisabled && variant === 'secondary' && 'hover:bg-accent-muted',
        isDisabled && variant === 'transparent' && 'hover:bg-white',
        block && 'w-full',
        !block && align === 'right' && 'ml-auto',
        !block && align === 'left' && 'mr-auto',
        size === 'thin'
          ? 'py-1 px-2'
          : size === 'large'
          ? 'w-full py-3 px-5'
          : size === 'small'
          ? 'py-2 px-3'
          : 'py-3 px-4',
        className
      )}
    >
      {icon && (
        <span
          className={classNames(
            iconPosition === 'left' && 'absolute inset-y-0 left-0 flex items-center pl-3',
            iconPosition === 'center' && 'absolute inset-0 flex items-center justify-center',
            iconPosition === 'right' && 'absolute inset-y-0 right-0 flex items-center pr-3',
            labelClass
          )}
        >
          {icon}
        </span>
      )}
      <Detail
        className={classNames(
          'text-semibold',
          loading && 'opacity-25',
          icon && iconPosition === 'left' && !loading ? 'pl-8' : '',
          icon && iconPosition === 'right' && !loading ? 'pr-8' : '',
          labelClass
        )}
      >
        {label}
      </Detail>
    </Element>
  )
}
